/**
 * Landing Page Component
 * Public landing page for Gongfu PM Simulation
 * Modern, positive, aspirational design with graphics and animations
 */

import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Landing.css';
import {
  MessageCircle,
  Target,
  BookOpen,
  FileText,
  ArrowRight,
  Users,
  Award,
  Shield,
  Play,
  Rocket,
  Star,
  Briefcase,
  CheckCircle2
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  
  const handleCTAClick = (type) => {
    // Navigate to login/signup with pricing context
    navigate('/login');
  };

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    // Also handle browser back/forward navigation
    const handlePopState = () => {
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Animation on scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav" aria-label="Main navigation">
        <div className="landing-nav-container">
          <div className="landing-logo">
            <img 
              src="/gongfu-logo.png" 
              alt="Gongfu" 
              className="logo-image"
              aria-hidden="true"
            />
            <span className="logo-text">GONGFU</span>
          </div>
          <div className="landing-nav-actions">
            <Link to="/login" className="nav-link" aria-label="Log in to your account">Log In</Link>
            <button 
              className="nav-cta" 
              onClick={scrollToPricing}
              aria-label="View pricing options"
            >
              Land Your Dream Job
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-pattern"></div>
        </div>
        <div className="landing-container">
          <div className="hero-content animate-on-scroll">
            <h1 className="hero-headline">
              Build Valuable PM Experience <span className="gradient-text">Fast</span>
            </h1>
            <p className="hero-subheadline">
            Gain hands on work experience, level up your game, and land your dream job at a product-led company. Silicon Valley approved. No theory or quizzes, guaranteed.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">5</div>
                <div className="stat-label">Realistic Scenarios</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">100%</div>
                <div className="stat-label">Interview Ready</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">$129</div>
                <div className="stat-label">vs $1000+</div>
              </div>
            </div>
            <div className="hero-ctas">
              <button 
                className="cta-primary"
                onClick={scrollToPricing}
                aria-label="View pricing options"
              >
                Land your Dream Job
                <ArrowRight size={18} aria-hidden="true" className="cta-arrow" />
              </button>
            </div>
            <div className="hero-trust">
              <Shield size={16} aria-hidden="true" />
              <span>30 day money back guarantee</span>
            </div>
          </div>
          <div className="hero-visual animate-on-scroll">
            <div className="hero-mockup-container">
              <div className="mockup-frame">
                <div className="mockup-header">
                  <div className="mockup-dots">
                    <span></span><span></span><span></span>
                  </div>
                </div>
                <div className="mockup-content">
                  <div className="mockup-chat">
                    <div className="chat-message stakeholder">
                      <div className="message-avatar">JD</div>
                      <div className="message-bubble">
                        <p>Hi, welcome to the team. You free to discuss the AI categorization feature leadership wants us to ship?</p>
                      </div>
                    </div>
                    <div className="chat-message user">
                      <div className="message-bubble">
                        <p>Hey, excited to be on the team. I've analyzed the user metrics and have a hypothesis...</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mockup-badge">
                <Play size={14} />
                <span>See it in action</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicator Section */}
      <section className="landing-trust-indicator" aria-labelledby="trust-heading">
        <div className="landing-container">
          <div className="trust-indicator-content animate-on-scroll">
            <p id="trust-heading" className="trust-indicator-text">
              Learn to think and speak like PMs at
            </p>
            <div className="trust-logos">
              <img
                src="/Trust-Signal-Logos-Gongfu.png"
                alt="Trusted by teams at Amazon, Canva, Chime, Coinbase, Dropbox, Duolingo, eBay, Figma, Google, Meta, N26, Netflix, Notion, Open AI, PayPal, Plaid, Ramp, Revolut, Stripe, Uber"
                className="trust-logo-image"
                loading="lazy"
                width="1100"
                height="320"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="landing-social-proof">
        <div className="landing-container">
          <div className="section-header animate-on-scroll">
            <h2 className="section-headline">Join PMs Building Real Experience</h2>
            <p className="section-subheadline">Built for PMs who want to get promoted and land their dream job</p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card animate-on-scroll">
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="testimonial-text">
                "I've struggled to tell stories in my interviews about the few results I've achieved as a PM. Love that I can now tell stories about my experience and feel confident in front of hiring teams. The stakeholder communication practice is exactly what I needed."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">JP</div>
                <div className="author-info">
                  <div className="author-name">Product Manager</div>
                  <div className="author-role">2-3 years experience</div>
                </div>
              </div>
            </div>
            <div className="testimonial-card animate-on-scroll">
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="testimonial-text">
                "Love the portfolio artifacts. Showing hiring managers real examples of my work and product sense is a game changer."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">RL</div>
                <div className="author-info">
                  <div className="author-name">Aspiring PM</div>
                  <div className="author-role">Transitioning to PM</div>
                </div>
              </div>
            </div>
            <div className="testimonial-card animate-on-scroll">
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="testimonial-text">
                "Best $129 I've spent on my career. The scenarios are realistic and the AI stakeholders feel real."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">BM</div>
                <div className="author-info">
                  <div className="author-name">Senior PM</div>
                  <div className="author-role">Leveling up skills</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="landing-value-prop">
        <div className="landing-container">
          <div className="value-prop-content animate-on-scroll">
            <h2 className="section-headline">
              From Stuck <span className="gradient-text">To In Demand PM</span>
            </h2>
            <p className="value-prop-subheadline">
              You know the frameworks. You've read the books. Now practice with realistic AI-stakeholders and build a product portfolio that proves you can do the work.
            </p>
            <div className="value-cards">
              <div className="value-card">
                <div className="value-icon">
                  <Briefcase size={32} />
                </div>
                <h3>Build Career Credentials</h3>
                <p>Receive personalized STAR case studies and results you can show hiring managers.</p>
              </div>
              <div className="value-card">
                <div className="value-icon">
                  <Users size={32} />
                </div>
                <h3>Master Communication</h3>
                <p>Practice real stakeholder management with AI-powered engineers, designers, and executives.</p>
              </div>
              <div className="value-card">
                <div className="value-icon">
                  <Target size={32} />
                </div>
                <h3>Apply Best Practices</h3>
                <p>Adventure through the product lifecycle from discovery to launch, see the results of your decision making.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Love Letter to PMs Section */}
      <section className="landing-love-letter">
        <div className="landing-container">
          <div className="love-letter-content animate-on-scroll">
            <h2 className="section-headline love-letter-headline">
              You're not behind. You're just in the <span className="gradient-text">wrong environment.</span>
            </h2>
            <p className="love-letter-text">
              You've studied Lenny's newsletter. You've applied the frameworks. But your company isn't empowering you the way product-led companies do. So you sit at your desk, deflated. Feeling like you're not cut out for this.
            </p>
            <p className="love-letter-recognition">Sound familiar?</p>
            <p className="love-letter-highlight">
              <strong>You can't grow your craft in the wrong environment.</strong>
            </p>
            <p className="love-letter-text">
              When companies don't understand product, it's almost impossible to get the results that would get you a role at one that does. It's not a you problem. It's an environment problem.
            </p>
            <p className="love-letter-solution">
              Gongfu gives you the unfair advantage. The right environment to build results and experience that lands your dream job.
            </p>
          </div>
        </div>
      </section>

      {/* Skills You'll Build Section */}
      <section className="landing-skills">
        <div className="landing-container">
          <div className="section-header animate-on-scroll">
            <h2 className="section-headline">Skills You'll Build<span className="gradient-text"> Fast</span></h2>
            <p className="section-subheadline">Feel your confidence grow and shine in interviews</p>
          </div>
          <div className="skills-grid animate-on-scroll">
            <span className="skill-pill">Strategic Thinking</span>
            <span className="skill-pill">Data Analysis</span>
            <span className="skill-pill">Roadmapping</span>
            <span className="skill-pill">Agile Sprint Planning</span>
            <span className="skill-pill">Design Sprints</span>
            <span className="skill-pill">Stakeholder Management</span>
            <span className="skill-pill">Effective Communication</span>
            <span className="skill-pill">Prioritization</span>
            <span className="skill-pill">Handling Tradeoffs</span>
            <span className="skill-pill">Go-To-Market (GTM)</span>
            <span className="skill-pill">Conflict Resolution</span>
            <span className="skill-pill">User Research</span>
            <span className="skill-pill">PRDs & Documentation</span>
          </div>
        </div>
      </section>

      {/* Scenarios Section */}
      <section className="landing-scenarios">
        <div className="landing-container">
          <div className="section-header animate-on-scroll">
            <h2 className="section-headline">Scenarios You'll Work Through</h2>
            <p className="section-subheadline">Five scenarios. One product lifecycle</p>
          </div>
          <div className="scenarios-list animate-on-scroll">
            <div className="scenario-row">
              <span className="scenario-num">1</span>
              <div className="scenario-content">
                <h3 className="scenario-title">Discovery & Definition</h3>
                <p className="scenario-tagline">Validate the problem with data and stakeholders. Ship a product brief.</p>
              </div>
            </div>
            <div className="scenario-row">
              <span className="scenario-num">2</span>
              <div className="scenario-content">
                <h3 className="scenario-title">Agile Sprint Planning & Conflict Resolution</h3>
                <p className="scenario-tagline">Turn the brief into sprint work. Resolve conflicts between eng, marketing, and leadership.</p>
              </div>
            </div>
            <div className="scenario-row">
              <span className="scenario-num">3</span>
              <div className="scenario-content">
                <h3 className="scenario-title">Design Sprint & Collaborative Workshops</h3>
                <p className="scenario-tagline">Run a design sprint. Facilitate workshops, prototype, test with users.</p>
              </div>
            </div>
            <div className="scenario-row">
              <span className="scenario-num">4</span>
              <div className="scenario-content">
                <h3 className="scenario-title">Development Sprint & GTM Planning</h3>
                <p className="scenario-tagline">Run the build, unblock engineers, plan go-to-market and launch.</p>
              </div>
            </div>
            <div className="scenario-row">
              <span className="scenario-num">5</span>
              <div className="scenario-content">
                <h3 className="scenario-title">Launch Analysis & Monetization Strategy</h3>
                <p className="scenario-tagline">Analyze results, segment by value, design pricing and rollout.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="landing-how-it-works">
        <div className="landing-container">
          <div className="section-header animate-on-scroll">
            <h2 className="section-headline">How Gongfu Works</h2>
            <p className="section-subheadline">A real world product management simulator</p>
          </div>
          <div className="how-it-works-steps">
            <div className="how-it-works-step animate-on-scroll">
              <div className="step-number-badge">
                <FileText size={24} />
              </div>
              <div className="step-content">
                <h3>Dive Right In</h3>
                <p>Begin with discovery and definition, and progress through 4 more scenarios that tell a complete product story.</p>
              </div>
            </div>

            <div className="how-it-works-step animate-on-scroll">
              <div className="step-number-badge">
                <MessageCircle size={24} />
              </div>
              <div className="step-content">
                <h3>Work with Real Stakeholders</h3>
                <p>Chat with AI-powered engineers, designers, and executives in Slack-style channels. Gather information, make decisions, and navigate real PM challenges.</p>
              </div>
            </div>

            <div className="how-it-works-step animate-on-scroll">
              <div className="step-number-badge">
                <BookOpen size={24} />
              </div>
              <div className="step-content">
                <h3>Build Your Artifacts</h3>
                <p>Create PRDs, user journey maps, and product briefs. Upload them to complete tasks and get feedback from your AI product mentor.</p>
              </div>
            </div>

            <div className="how-it-works-step animate-on-scroll">
              <div className="step-number-badge">
                <Award size={24} />
              </div>
              <div className="step-content">
                <h3>Interview Ready Case Studies</h3>
                <p>Complete a scenario and recieve a personalized STAR-format case study you can use in your interviews and CV.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing-section" className="landing-pricing">
        <div className="landing-container">
          <div className="section-header animate-on-scroll">
            <h2 className="section-headline">Fast Track Your PM Experience</h2>
            <p className="section-subheadline">One time payment</p>
          </div>
          <div className="pricing-grid pricing-single">
            <div className="pricing-card featured animate-on-scroll">
              <div className="pricing-header">
                <h3>Premium Product Manager</h3>
                <div className="pricing-price">
                  <span className="price-amount">$129</span>
                  <span className="price-period">One-time</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>
                  <CheckCircle2 size={18} />
                  <span>All 5 SaaS scenarios (full product lifecycle)</span>
                </li>
                <li>
                  <CheckCircle2 size={18} />
                  <span>All future scenarios (as they're released)</span>
                </li>
                <li>
                  <CheckCircle2 size={18} />
                  <span>Full stakeholder communication experience</span>
                </li>
                <li>
                  <CheckCircle2 size={18} />
                  <span>AI product mentor access</span>
                </li>
                <li>
                  <CheckCircle2 size={18} />
                  <span>Interview ready artefacts for CV</span>
                </li>
                <li>
                  <CheckCircle2 size={18} />
                  <span>Full skill development dashboard & progress tracking</span>
                </li>
              </ul>
              <button 
                className="pricing-cta primary"
                onClick={() => handleCTAClick('all')}
                aria-label="Get full access for $129"
              >
                Get Full Access — $129
              </button>
            </div>
          </div>
          <div className="pricing-guarantee animate-on-scroll">
            <Shield size={20} aria-hidden="true" />
            <p>30-day money-back guarantee</p>
            <span className="pricing-comparison">vs. $1,500+ for traditional PM courses</span>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="landing-faq">
        <div className="landing-container">
          <div className="section-header animate-on-scroll">
            <h2 className="section-headline">FAQs</h2>
            <p className="section-subheadline">Everything you need to know</p>
          </div>
          <div className="faq-grid">
            <div className="faq-item animate-on-scroll">
              <h3>What does Gongfu mean?</h3>
              <p>Gongfu (功夫) is an ancient Chinese term meaning "skill achieved through hard work and practice". Just like martial artists develop gongfu through consistent practice, we help PMs build real product management skills through hands-on simulations and real-world practice.</p>
            </div>
            <div className="faq-item animate-on-scroll">
              <h3>How is this different from other PM courses and simulators?</h3>
              <p>Gongfu focuses on hands-on practice with real stakeholder communication and live skill development, not just theory. You build real results and portfolio artifacts you can use in job applications, and practice the communication skills that get you hired.</p>
            </div>
            <div className="faq-item animate-on-scroll">
              <h3>Do I need coding experience?</h3>
              <p>No coding experience required. Gongfu focuses on product management skills: communication, strategy, data analysis, prioritization, and stakeholder management.The skills that matter most for PM roles.</p>
            </div>
            <div className="faq-item animate-on-scroll">
              <h3>Can I use the portfolio artifacts in job applications?</h3>
              <p>Absolutely! All portfolio artifacts are generated in STAR format, perfect for resumes, cover letters, and interview preparation. They're designed to show hiring managers exactly what you can do.</p>
            </div>
            <div className="faq-item animate-on-scroll">
              <h3>What if I'm not a PM yet?</h3>
              <p>Gongfu is perfect for aspiring PMs. The scenarios start at beginner level and build up, teaching you PM skills as you practice.</p>
            </div>
            <div className="faq-item animate-on-scroll">
              <h3>How long does each scenario take?</h3>
              <p>Each scenario typically takes 2-4 hours to complete, depending on how deeply you engage with stakeholders and create artifacts. You can work at your own pace.</p>
            </div>
            <div className="faq-item animate-on-scroll">
              <h3>Is there a money-back guarantee?</h3>
              <p>Yes! We offer a 30 day money back guarantee. If you're not satisfied with Gongfu, we'll refund your payment. No questions asked.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="landing-final-cta">
        <div className="cta-background">
          <div className="cta-gradient"></div>
          <div className="cta-pattern"></div>
        </div>
        <div className="landing-container">
          <div className="cta-content animate-on-scroll">
            <div className="cta-icon">
              <Rocket size={48} />
            </div>
            <h2 className="cta-headline">Ready to Build PM Experience Fast?</h2>
            <p className="cta-subheadline">
              One payment of $129. Full access to all scenarios. Build portfolio artifacts. Land your dream PM role.
            </p>
            <div className="cta-buttons">
              <button 
                className="cta-primary large"
                onClick={scrollToPricing}
                aria-label="View pricing — $129"
              >
                Get Full Access — $129
                <ArrowRight size={20} aria-hidden="true" className="cta-arrow" />
              </button>
            </div>
            <div className="cta-trust">
              <Shield size={16} aria-hidden="true" />
              <span>30-day money back guarantee</span>
              <span className="trust-separator" aria-hidden="true">•</span>
              <span>One-time payment.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo-container">
                <img 
                  src="/gongfu-logo.png" 
                  alt="Gongfu" 
                  className="logo-image footer-logo"
                  aria-label="Gongfu"
                />
                <span className="logo-text">GONGFU</span>
              </div>
              <p>Build valuable PM experience fast.</p>
            </div>
            <div className="footer-links">
              <Link to="/login" className="footer-link">Log In</Link>
              <button type="button" className="footer-link" onClick={() => {}}>Privacy Policy</button>
              <button type="button" className="footer-link" onClick={() => {}}>Terms of Service</button>
            </div>
          </div>
          <p className="footer-copyright">&copy; 2026 Gongfu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
